import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router';
import {
  adminBookBuilderApi,
  type AdminBookAsset,
  type AdminBookAssetKind,
  type AdminBookLayout,
  type AdminBookTextPosition,
  type PublishedAdminBook,
  type SavedAdminBook,
} from '../api/admin-book-builder.js';
import { userErrorMessage } from '../api/errors.js';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';

interface SpreadDraft {
  readonly key: string;
  readonly id: string | null;
  readonly primaryTextRu: string;
  readonly secondaryTextRu: string;
  readonly layoutId: AdminBookLayout | null;
  readonly textPosition: AdminBookTextPosition;
  readonly secondaryTextPosition: AdminBookTextPosition;
  readonly bandImageRatio: number;
  readonly image: AdminBookAsset | null;
  readonly video: AdminBookAsset | null;
  readonly narration: AdminBookAsset | null;
}

export function AdminBookBuilderPage(): JSX.Element {
  const { user } = useAuth();
  const { t } = useI18n();
  const copy = t.admin.bookBuilder;
  const { ownerId: routeOwnerId, id: bookId } = useParams();
  const editing = Boolean(bookId);
  const initializedBook = useRef<string | null>(null);
  const [ownerId, setOwnerId] = useState(routeOwnerId ?? user?.id ?? '');
  const [styleId, setStyleId] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [dedication, setDedication] = useState('');
  const [defaultLayout, setDefaultLayout] = useState<AdminBookLayout>('full-bleed');
  const [cover, setCover] = useState<AdminBookAsset | null>(null);
  const [backCover, setBackCover] = useState<AdminBookAsset | null>(null);
  const [spreads, setSpreads] = useState<readonly SpreadDraft[]>([newSpread()]);
  const [published, setPublished] = useState<PublishedAdminBook | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!ownerId && (routeOwnerId || user?.id)) setOwnerId(routeOwnerId ?? user?.id ?? '');
  }, [ownerId, routeOwnerId, user?.id]);

  const editableBook = useQuery({
    queryKey: ['admin-editable-book', bookId],
    queryFn: () => adminBookBuilderApi.editableBook(bookId!),
    enabled: user?.role === 'admin' && editing,
  });
  useEffect(() => {
    const book = editableBook.data;
    if (!book || initializedBook.current === book.bookId) return;
    initializedBook.current = book.bookId;
    setOwnerId(book.ownerId);
    setStyleId(book.styleId);
    setTitle(book.title);
    setSubtitle(book.subtitle ?? '');
    setDedication(book.dedication ?? '');
    setDefaultLayout(book.defaultLayout);
    setCover(book.cover);
    setBackCover(book.backCover);
    setSpreads(
      book.spreads.map((spread) => ({
        key: spread.id,
        id: spread.id,
        primaryTextRu: spread.textBlocks[0]?.textRu ?? '',
        secondaryTextRu: spread.textBlocks[1]?.textRu ?? '',
        layoutId: spread.layoutId,
        textPosition:
          spread.textBlocks[0]?.position ?? defaultPosition(spread.layoutId ?? book.defaultLayout),
        secondaryTextPosition:
          spread.textBlocks[1]?.position ??
          alternativePosition(
            spread.layoutId ?? book.defaultLayout,
            spread.textBlocks[0]?.position ??
              defaultPosition(spread.layoutId ?? book.defaultLayout),
          ),
        bandImageRatio: spread.bandImageRatio ?? 0.7,
        image: spread.image,
        video: spread.video,
        narration: spread.narration,
      })),
    );
  }, [editableBook.data]);

  const options = useQuery({
    queryKey: ['admin-book-builder-options', ownerId],
    queryFn: () => adminBookBuilderApi.options(ownerId || undefined),
    enabled: user?.role === 'admin',
  });
  useEffect(() => {
    const styles = options.data?.styles ?? [];
    if (styles.length > 0 && !styles.some((style) => style.id === styleId)) {
      setStyleId(styles[0]!.id);
    }
  }, [options.data?.styles, styleId]);

  const uploadCount = useMemo(
    () =>
      Number(Boolean(cover)) +
      Number(Boolean(backCover)) +
      spreads.reduce(
        (sum, spread) =>
          sum +
          Number(Boolean(spread.image)) +
          Number(Boolean(spread.video)) +
          Number(Boolean(spread.narration)),
        0,
      ),
    [backCover, cover, spreads],
  );

  const publish = useMutation<PublishedAdminBook | SavedAdminBook, Error, void>({
    mutationFn: () => {
      const validation = validate(
        { ownerId, styleId, title, defaultLayout, cover, backCover, spreads },
        copy,
      );
      if (validation) throw new Error(validation);
      const input = {
        ownerId,
        styleId,
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        dedication: dedication.trim() || null,
        defaultLayout,
        coverAssetId: cover!.id,
        backCoverAssetId: backCover!.id,
        spreads: spreads.map((spread) => ({
          id: spread.id,
          layoutId: spread.layoutId,
          textBlocks:
            (spread.layoutId ?? defaultLayout) === 'full-bleed'
              ? [
                  { textRu: spread.primaryTextRu.trim(), position: spread.textPosition },
                  {
                    textRu: spread.secondaryTextRu.trim(),
                    position: spread.secondaryTextPosition,
                  },
                ]
              : [{ textRu: spread.primaryTextRu.trim(), position: spread.textPosition }],
          bandImageRatio:
            (spread.layoutId ?? defaultLayout) === 'band' ? spread.bandImageRatio : null,
          imageAssetId: spread.image!.id,
          videoAssetId: spread.video?.id ?? null,
          narrationAssetId: spread.narration?.id ?? null,
        })),
      };
      return editing
        ? adminBookBuilderApi.save(bookId!, input)
        : adminBookBuilderApi.publish(input);
    },
    onMutate: () => {
      setFormError(null);
      setPublished(null);
      setSavedAt(null);
    },
    onSuccess: (result) => {
      if ('publicUrl' in result) setPublished(result);
      else setSavedAt(result.updatedAt);
    },
    onError: (error: Error) =>
      setFormError(error.name === 'Error' ? error.message : userErrorMessage(error, t)),
  });

  if (user?.role !== 'admin') {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p role="alert">{t.admin.forbidden}</p>
        <Link to="/app" className="kitup-quiet-button mt-4">
          {t.admin.toApp}
        </Link>
      </main>
    );
  }
  if (editing && editableBook.isPending) return <p>{t.common.loading}</p>;
  if (editing && editableBook.isError) return <p role="alert">{t.common.unexpectedError}</p>;

  const updateSpread = (key: string, patch: Partial<Omit<SpreadDraft, 'key'>>): void => {
    setSpreads((current) =>
      current.map((spread) => (spread.key === key ? { ...spread, ...patch } : spread)),
    );
  };
  const moveSpread = (index: number, direction: -1 | 1): void => {
    setSpreads((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  };

  return (
    <main className="mx-auto max-w-[1500px] p-4 sm:p-6">
      <div className="grid items-start gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <AdminSidebar />

        <div className="min-w-0">
          <header className="mb-6">
            <h1 className="text-2xl font-semibold">{editing ? copy.editTitle : copy.title}</h1>
            <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">
              {editing ? copy.editSubtitle : copy.subtitle}
            </p>
          </header>

          <section className="kitup-card grid gap-4 p-5 md:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-medium">
              <span>{copy.owner}</span>
              <select
                value={ownerId}
                disabled={editing || uploadCount > 0}
                onChange={(event) => setOwnerId(event.target.value)}
              >
                {(options.data?.users ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} · {item.email}
                  </option>
                ))}
              </select>
              {editing || uploadCount > 0 ? <small>{copy.ownerLocked}</small> : null}
            </label>
            <label className="grid gap-1.5 text-sm font-medium">
              <span>{copy.style}</span>
              <select value={styleId} onChange={(event) => setStyleId(event.target.value)}>
                {(options.data?.styles ?? []).map((style) => (
                  <option key={style.id} value={style.id}>
                    {style.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-medium">
              <span>{copy.defaultLayout}</span>
              <select
                aria-label={copy.defaultLayout}
                value={defaultLayout}
                onChange={(event) => {
                  const next = event.target.value as AdminBookLayout;
                  const previous = defaultLayout;
                  setDefaultLayout(next);
                  setSpreads((current) =>
                    current.map((spread) =>
                      spread.layoutId === null
                        ? { ...spread, ...adaptSpreadLayout(previous, next, spread) }
                        : spread,
                    ),
                  );
                }}
              >
                {LAYOUTS.map((layout) => (
                  <option key={layout} value={layout}>
                    {copy.layouts[layout]}
                  </option>
                ))}
              </select>
              <small>{copy.defaultLayoutHint}</small>
            </label>
            <TextField label={copy.bookTitle} value={title} required onChange={setTitle} />
            <TextField label={copy.bookSubtitle} value={subtitle} onChange={setSubtitle} />
            <label className="grid gap-1.5 text-sm font-medium md:col-span-2">
              <span>{copy.dedication}</span>
              <textarea
                rows={3}
                value={dedication}
                onChange={(event) => setDedication(event.target.value)}
              />
            </label>
          </section>

          <section className="mt-6">
            <h2 className="text-lg font-semibold">{copy.covers}</h2>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <AssetField
                ownerId={ownerId}
                kind="cover_image"
                label={copy.frontCover}
                accept="image/jpeg,image/png,image/webp"
                value={cover}
                onChange={setCover}
              />
              <AssetField
                ownerId={ownerId}
                kind="cover_image"
                label={copy.backCover}
                accept="image/jpeg,image/png,image/webp"
                value={backCover}
                onChange={setBackCover}
              />
            </div>
          </section>

          <section className="mt-7">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{copy.spreads}</h2>
                <p className="text-sm text-[var(--kitup-color-muted)]">{copy.spreadsHint}</p>
              </div>
              <button
                type="button"
                className="kitup-secondary-button"
                onClick={() => setSpreads((current) => [...current, newSpread()])}
              >
                {copy.addSpread}
              </button>
            </div>

            <div className="mt-4 grid gap-5">
              {spreads.map((spread, index) => (
                <article key={spread.key} className="kitup-card p-5">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-semibold">{copy.spread(index + 1)}</h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="kitup-quiet-button"
                        disabled={index === 0}
                        onClick={() => moveSpread(index, -1)}
                      >
                        {copy.up}
                      </button>
                      <button
                        type="button"
                        className="kitup-quiet-button"
                        disabled={index === spreads.length - 1}
                        onClick={() => moveSpread(index, 1)}
                      >
                        {copy.down}
                      </button>
                      <button
                        type="button"
                        className="kitup-quiet-button"
                        disabled={spreads.length === 1}
                        onClick={() =>
                          setSpreads((current) => current.filter((item) => item.key !== spread.key))
                        }
                      >
                        {copy.remove}
                      </button>
                    </div>
                  </div>
                  {(spread.layoutId ?? defaultLayout) === 'full-bleed' ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="grid gap-1.5 text-sm font-medium">
                        <span>{copy.primaryBlockText}</span>
                        <textarea
                          rows={6}
                          value={spread.primaryTextRu}
                          onChange={(event) =>
                            updateSpread(spread.key, { primaryTextRu: event.target.value })
                          }
                        />
                      </label>
                      <label className="grid gap-1.5 text-sm font-medium">
                        <span>{copy.secondaryBlockText}</span>
                        <textarea
                          rows={6}
                          value={spread.secondaryTextRu}
                          onChange={(event) =>
                            updateSpread(spread.key, { secondaryTextRu: event.target.value })
                          }
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="grid gap-1.5 text-sm font-medium">
                      <span>{copy.spreadText}</span>
                      <textarea
                        rows={6}
                        value={spread.primaryTextRu}
                        onChange={(event) =>
                          updateSpread(spread.key, { primaryTextRu: event.target.value })
                        }
                      />
                    </label>
                  )}
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="grid gap-1.5 text-sm font-medium">
                      <span>{copy.spreadLayout}</span>
                      <select
                        value={spread.layoutId ?? ''}
                        onChange={(event) => {
                          const next = (event.target.value || null) as AdminBookLayout | null;
                          const previous = spread.layoutId ?? defaultLayout;
                          const effective = next ?? defaultLayout;
                          updateSpread(spread.key, {
                            layoutId: next,
                            ...adaptSpreadLayout(previous, effective, spread),
                          });
                        }}
                      >
                        <option value="">{copy.inheritLayout(copy.layouts[defaultLayout])}</option>
                        {LAYOUTS.map((layout) => (
                          <option key={layout} value={layout}>
                            {copy.layouts[layout]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-1.5 text-sm font-medium">
                      <span>
                        {(spread.layoutId ?? defaultLayout) === 'full-bleed'
                          ? copy.primaryTextPosition
                          : copy.textPosition}
                      </span>
                      <select
                        value={spread.textPosition}
                        onChange={(event) => {
                          const textPosition = event.target.value as AdminBookTextPosition;
                          updateSpread(spread.key, {
                            textPosition,
                            secondaryTextPosition:
                              textPosition === spread.secondaryTextPosition
                                ? alternativePosition(
                                    spread.layoutId ?? defaultLayout,
                                    textPosition,
                                  )
                                : spread.secondaryTextPosition,
                          });
                        }}
                      >
                        {positionsFor(spread.layoutId ?? defaultLayout).map((position) => (
                          <option key={position} value={position}>
                            {copy.positions[position]}
                          </option>
                        ))}
                      </select>
                    </label>
                    {(spread.layoutId ?? defaultLayout) === 'full-bleed' ? (
                      <label className="grid gap-1.5 text-sm font-medium md:col-start-2">
                        <span>{copy.secondaryTextPosition}</span>
                        <select
                          aria-label={copy.secondaryTextPosition}
                          value={spread.secondaryTextPosition}
                          onChange={(event) =>
                            updateSpread(spread.key, {
                              secondaryTextPosition: event.target.value as AdminBookTextPosition,
                            })
                          }
                        >
                          {positionsFor('full-bleed')
                            .filter((position) => position !== spread.textPosition)
                            .map((position) => (
                              <option key={position} value={position}>
                                {copy.positions[position]}
                              </option>
                            ))}
                        </select>
                      </label>
                    ) : null}
                    {(spread.layoutId ?? defaultLayout) === 'band' ? (
                      <label className="grid gap-1.5 text-sm font-medium md:col-span-2">
                        <span>
                          {copy.bandImageRatio}: {Math.round(spread.bandImageRatio * 100)}%
                        </span>
                        <input
                          type="range"
                          min="0.5"
                          max="0.85"
                          step="0.05"
                          value={spread.bandImageRatio}
                          onChange={(event) =>
                            updateSpread(spread.key, {
                              bandImageRatio: Number(event.target.value),
                            })
                          }
                        />
                      </label>
                    ) : null}
                  </div>
                  <div className="mt-4 grid gap-4 xl:grid-cols-3">
                    <AssetField
                      ownerId={ownerId}
                      kind="scene_image"
                      label={copy.illustration}
                      accept="image/jpeg,image/png,image/webp"
                      value={spread.image}
                      onChange={(image) => updateSpread(spread.key, { image })}
                    />
                    <AssetField
                      ownerId={ownerId}
                      kind="scene_video"
                      label={copy.videoOptional}
                      accept="video/mp4,video/webm"
                      value={spread.video}
                      onChange={(video) => updateSpread(spread.key, { video })}
                    />
                    <AssetField
                      ownerId={ownerId}
                      kind="narration_audio"
                      label={copy.narrationOptional}
                      accept="audio/mpeg,audio/wav,audio/mp4,audio/ogg,audio/webm"
                      value={spread.narration}
                      onChange={(narration) => updateSpread(spread.key, { narration })}
                    />
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="kitup-card sticky bottom-4 mt-7 flex flex-wrap items-center justify-between gap-4 p-5 shadow-lg">
            <div>
              <p className="font-medium">{copy.readySummary(spreads.length, uploadCount)}</p>
              <p className="text-sm text-[var(--kitup-color-muted)]">{copy.publishHint}</p>
            </div>
            <button
              type="button"
              className="kitup-primary-button"
              disabled={publish.isPending || options.isPending}
              onClick={() => publish.mutate()}
            >
              {publish.isPending
                ? editing
                  ? copy.saving
                  : copy.publishing
                : editing
                  ? copy.save
                  : copy.publish}
            </button>
          </section>

          {formError ? (
            <p role="alert" className="mt-4 text-sm text-red-700">
              {formError}
            </p>
          ) : null}
          {savedAt ? (
            <section className="kitup-card mt-5 border border-emerald-300 p-5">
              <h2 className="font-semibold text-emerald-800">{copy.saved}</h2>
              <Link
                to={`/admin/accounts/${ownerId}/books/${bookId}/preview`}
                className="kitup-primary-button mt-3"
              >
                {copy.openPreview}
              </Link>
            </section>
          ) : null}
          {published ? (
            <section className="kitup-card mt-5 border border-emerald-300 p-5">
              <h2 className="font-semibold text-emerald-800">{copy.published}</h2>
              <a
                className="mt-2 block break-all underline"
                href={published.publicUrl}
                target="_blank"
                rel="noreferrer"
              >
                {published.publicUrl}
              </a>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/app/books/${published.bookId}/preview`}
                  className="kitup-primary-button"
                >
                  {copy.openPreview}
                </Link>
                <button
                  type="button"
                  className="kitup-secondary-button"
                  onClick={() => void navigator.clipboard.writeText(published.publicUrl)}
                >
                  {copy.copyLink}
                </button>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
}

function AdminSidebar(): JSX.Element {
  const { t } = useI18n();
  return (
    <aside className="kitup-card grid gap-1 p-3 lg:sticky lg:top-4">
      <Link className="kitup-quiet-button justify-start" to="/admin/book-builder">
        {t.admin.bookBuilder.title}
      </Link>
      <Link className="kitup-quiet-button justify-start" to="/admin/accounts">
        {t.admin.accounts.title}
      </Link>
      <Link className="kitup-quiet-button justify-start" to="/admin/usage">
        {t.admin.usage.title}
      </Link>
      <Link className="kitup-quiet-button justify-start" to="/admin/promotions">
        {t.admin.promotions.title}
      </Link>
      <Link className="kitup-quiet-button justify-start" to="/admin/generations">
        {t.admin.title}
      </Link>
      <Link className="kitup-quiet-button justify-start" to="/app">
        {t.admin.toApp}
      </Link>
    </aside>
  );
}

function AssetField({
  ownerId,
  kind,
  label,
  accept,
  value,
  onChange,
}: {
  readonly ownerId: string;
  readonly kind: AdminBookAssetKind;
  readonly label: string;
  readonly accept: string;
  readonly value: AdminBookAsset | null;
  readonly onChange: (asset: AdminBookAsset | null) => void;
}): JSX.Element {
  const { t } = useI18n();
  const copy = t.admin.bookBuilder;
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const select = async (file?: File): Promise<void> => {
    if (!file || !ownerId) return;
    setUploading(true);
    setError(null);
    try {
      onChange(await adminBookBuilderApi.upload(ownerId, kind, file));
    } catch (uploadError: unknown) {
      setError(userErrorMessage(uploadError, t));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-[var(--kitup-color-muted)]/25 p-3">
      <p className="text-sm font-medium">{label}</p>
      {value ? (
        <AssetPreview asset={value} />
      ) : (
        <p className="my-3 text-sm text-[var(--kitup-color-muted)]">{copy.noFile}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="kitup-secondary-button cursor-pointer">
          {uploading ? copy.uploading : value ? copy.replaceFile : copy.chooseFile}
          <input
            className="sr-only"
            type="file"
            accept={accept}
            disabled={uploading || !ownerId}
            onChange={(event) => {
              void select(event.target.files?.[0]);
              event.currentTarget.value = '';
            }}
          />
        </label>
        {value ? (
          <button type="button" className="kitup-quiet-button" onClick={() => onChange(null)}>
            {copy.clearFile}
          </button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function AssetPreview({ asset }: { readonly asset: AdminBookAsset }): JSX.Element {
  if (asset.kind === 'cover_image' || asset.kind === 'scene_image') {
    return (
      <img
        src={asset.url}
        alt=""
        className="my-3 aspect-video w-full rounded-md bg-slate-100 object-cover"
      />
    );
  }
  if (asset.kind === 'scene_video') {
    return (
      <video
        src={asset.url}
        className="my-3 aspect-video w-full rounded-md bg-black object-contain"
        controls
        preload="metadata"
      />
    );
  }
  return <audio src={asset.url} className="my-3 w-full" controls preload="metadata" />;
}

function TextField({
  label,
  value,
  required = false,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly required?: boolean;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label className="grid gap-1.5 text-sm font-medium">
      <span>{label}</span>
      <input required={required} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function newSpread(): SpreadDraft {
  return {
    key:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : String(Date.now()),
    id: null,
    primaryTextRu: '',
    secondaryTextRu: '',
    layoutId: null,
    textPosition: 'top-left',
    secondaryTextPosition: 'bottom-right',
    bandImageRatio: 0.7,
    image: null,
    video: null,
    narration: null,
  };
}

const LAYOUTS: readonly AdminBookLayout[] = ['full-bleed', 'split', 'band'];

const TEXT_POSITIONS: Readonly<Record<AdminBookLayout, readonly AdminBookTextPosition[]>> = {
  split: ['left', 'right'],
  'full-bleed': ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
  band: ['top', 'bottom'],
};

function positionsFor(layout: AdminBookLayout): readonly AdminBookTextPosition[] {
  return TEXT_POSITIONS[layout];
}

function defaultPosition(layout: AdminBookLayout): AdminBookTextPosition {
  return positionsFor(layout)[0]!;
}

function alternativePosition(
  layout: AdminBookLayout,
  position: AdminBookTextPosition,
): AdminBookTextPosition {
  if (layout === 'full-bleed' && position !== 'bottom-right') return 'bottom-right';
  return (
    positionsFor(layout).find((candidate) => candidate !== position) ?? defaultPosition(layout)
  );
}

function validTextPositions(
  layout: AdminBookLayout,
  spread: Pick<SpreadDraft, 'textPosition' | 'secondaryTextPosition'>,
): Pick<SpreadDraft, 'textPosition' | 'secondaryTextPosition'> {
  const textPosition = positionsFor(layout).includes(spread.textPosition)
    ? spread.textPosition
    : defaultPosition(layout);
  const secondaryTextPosition =
    layout === 'full-bleed' &&
    positionsFor(layout).includes(spread.secondaryTextPosition) &&
    spread.secondaryTextPosition !== textPosition
      ? spread.secondaryTextPosition
      : alternativePosition(layout, textPosition);
  return { textPosition, secondaryTextPosition };
}

function adaptSpreadLayout(
  previous: AdminBookLayout,
  next: AdminBookLayout,
  spread: SpreadDraft,
): Pick<
  SpreadDraft,
  'primaryTextRu' | 'secondaryTextRu' | 'textPosition' | 'secondaryTextPosition'
> {
  let primaryTextRu = spread.primaryTextRu;
  let secondaryTextRu = spread.secondaryTextRu;
  if (previous === 'full-bleed' && next !== 'full-bleed') {
    primaryTextRu = [primaryTextRu.trim(), secondaryTextRu.trim()].filter(Boolean).join('\n\n');
    secondaryTextRu = '';
  }
  const positions = validTextPositions(next, spread);
  return { primaryTextRu, secondaryTextRu, ...positions };
}

function validate(
  input: {
    readonly ownerId: string;
    readonly styleId: string;
    readonly title: string;
    readonly defaultLayout: AdminBookLayout;
    readonly cover: AdminBookAsset | null;
    readonly backCover: AdminBookAsset | null;
    readonly spreads: readonly SpreadDraft[];
  },
  copy: ReturnType<typeof useI18n>['t']['admin']['bookBuilder'],
): string | null {
  if (!input.ownerId || !input.styleId || !input.title.trim()) return copy.validation.main;
  if (!input.cover || !input.backCover) return copy.validation.covers;
  const invalidIndex = input.spreads.findIndex(
    (spread) => !spread.primaryTextRu.trim() || !spread.image,
  );
  if (invalidIndex >= 0) return copy.validation.spread(invalidIndex + 1);
  const missingSecondIndex = input.spreads.findIndex(
    (spread) =>
      (spread.layoutId ?? input.defaultLayout) === 'full-bleed' && !spread.secondaryTextRu.trim(),
  );
  return missingSecondIndex >= 0 ? copy.validation.secondBlock(missingSecondIndex + 1) : null;
}
